/**
 * APR-2964/2965 — decisão de resolução de tenant do login, extraída pura para
 * ser testável sem Angular.
 *
 * Fases:
 *  - SHADOW (dynamicTenantResolution ausente/false): o `citiesWhitelist`
 *    hardcoded continua DECIDINDO; a API é consultada e comparada, e toda
 *    divergência vira telemetria. Zero mudança de comportamento.
 *  - DINÂMICO (true): a API decide; a whitelist vira fallback e CADA uso do
 *    fallback é alarme. Rollback = desligar a flag (sem deploy de código novo).
 *
 * O erro de hoje é silencioso por construção (identidade fora da lista =>
 * redirect mudo para '/'); a telemetria existe para produzir sinal ANTES da
 * inversão — o gate de A2 é 7 dias sem `legacy_only` nem `connection_mismatch`.
 */

export interface WhitelistEntry {
  name: string;
  identity: string;
  connection: string;
  loginUrl?: string;
}

export interface TenantResolutionInput {
  identity: string;
  whitelistEntry?: WhitelistEntry;
  /** Cidade resolvida pela API (GET cities/identity/:id) — null se a API não conhece. */
  apiCity: { index?: number; identidade?: string } | null;
  /** connection vinda do GET /v1/auth-config/:cityId (APR-2963) — undefined se sem realm. */
  apiConnection?: string;
  dynamicMode: boolean;
}

export type TenantResolutionEvent =
  | 'tenant_resolution_api_only'
  | 'tenant_resolution_legacy_only'
  | 'tenant_resolution_connection_mismatch'
  | 'tenant_resolution_fallback_used'
  | 'tenant_resolution_not_found';

export interface TenantResolutionDecision {
  allow: boolean;
  connection?: string;
  loginUrl?: string;
  source: 'whitelist' | 'api' | 'api-fallback-whitelist' | 'none';
  events: Array<{ event: TenantResolutionEvent; detail: Record<string, unknown> }>;
}

export function decideTenantResolution(input: TenantResolutionInput): TenantResolutionDecision {
  const events: TenantResolutionDecision['events'] = [];
  const { identity, whitelistEntry, apiCity, apiConnection, dynamicMode } = input;

  const apiKnows = Boolean(apiCity && apiConnection);

  if (whitelistEntry && !apiKnows) {
    // Lista conhece, API não => BUG DE BACKFILL (realm faltando). Bloqueia o gate de A2.
    events.push({
      event: 'tenant_resolution_legacy_only',
      detail: { identity, whitelistConnection: whitelistEntry.connection, apiCityFound: Boolean(apiCity) },
    });
  }

  if (!whitelistEntry && apiKnows) {
    // API conhece, lista não => cidade nova funcionando sem deploy (o objetivo).
    events.push({ event: 'tenant_resolution_api_only', detail: { identity, apiConnection } });
  }

  if (whitelistEntry && apiConnection && whitelistEntry.connection !== apiConnection) {
    // Errar a connection não degrada, DERRUBA o login (OneDocChapeco dispara
    // outro fluxo). Este evento tem que zerar antes da inversão.
    events.push({
      event: 'tenant_resolution_connection_mismatch',
      detail: { identity, whitelistConnection: whitelistEntry.connection, apiConnection },
    });
  }

  if (!dynamicMode) {
    // SHADOW: comportamento atual, byte a byte.
    if (!whitelistEntry) {
      events.push({ event: 'tenant_resolution_not_found', detail: { identity, mode: 'shadow' } });
      return { allow: false, source: 'none', events };
    }
    return {
      allow: true,
      connection: whitelistEntry.connection,
      loginUrl: whitelistEntry.loginUrl,
      source: 'whitelist',
      events,
    };
  }

  // DINÂMICO: a API decide.
  if (apiKnows) {
    return {
      allow: true,
      connection: apiConnection,
      // loginUrl (domínio custom) ainda não tem fonte na API — segue da lista
      // enquanto ela existir; persistir server-side é pré-requisito do A3.
      loginUrl: whitelistEntry?.loginUrl,
      source: 'api',
      events,
    };
  }

  if (whitelistEntry) {
    // Fallback com ALARME: cada uso é regressão de backfill, não log.
    events.push({
      event: 'tenant_resolution_fallback_used',
      detail: { identity, whitelistConnection: whitelistEntry.connection },
    });
    return {
      allow: true,
      connection: whitelistEntry.connection,
      loginUrl: whitelistEntry.loginUrl,
      source: 'api-fallback-whitelist',
      events,
    };
  }

  // FAIL-CLOSED: nada de cair numa cidade default errada em silêncio.
  events.push({ event: 'tenant_resolution_not_found', detail: { identity, mode: 'dynamic' } });
  return { allow: false, source: 'none', events };
}

/**
 * Emissor de telemetria: console estruturado (greppável no agregador de logs
 * do browser/RUM). `legacy_only`, `connection_mismatch` e `fallback_used` saem
 * como error — são os eventos que bloqueiam/alarmam o cutover.
 */
export function emitTenantResolutionEvents(decision: TenantResolutionDecision): void {
  for (const { event, detail } of decision.events) {
    const payload = JSON.stringify({ event, ...detail, ts: new Date().toISOString() });
    if (
      event === 'tenant_resolution_legacy_only' ||
      event === 'tenant_resolution_connection_mismatch' ||
      event === 'tenant_resolution_fallback_used'
    ) {
      console.error(`[tenant-resolution] ${payload}`);
    } else {
      console.warn(`[tenant-resolution] ${payload}`);
    }
  }
}
